/**
 * Wire types canonical — Protection Agent validation pré-envio outbound.
 *
 * Sprint Backend Protection v1 — Agent C (UI Composer) materializa Item 226
 * da Auditoria_Casos_Uso_2026-05-19.md §Frente 3 (e).
 *
 * Contrato definido por Agent A (PR-A backend protection-agent). Replicado
 * TEXTUALMENTE aqui para zero drift — ver `apps/engine/src/modules/ai-orchestration/protection-agent/`
 * + OpenAPI `paths/conversations.yaml` sendMessage response.
 *
 * Pattern Wire types feature-scoped — pós-Fatia G Brief 5 D-A 3-camadas
 * (NÃO compartilhar via `packages/shared` — type lives onde é consumido).
 *
 * Capability gate canonical: `ai-protection-outbound-validate` (Agent B PR-B —
 * Solo+ default conforme decisão Tech Lead).
 */

/** Severidade canonical retornada pelo protection-agent. */
export type ProtectionSeverity = 'none' | 'low' | 'medium' | 'high' | 'critical';

/**
 * Categorias canonical detectadas pelo protection-agent. String aberta para
 * extensibilidade — categorias core enumeradas, custom categories permitidas.
 */
export type ProtectionCategory =
  | 'pii_leak'
  | 'profanity'
  | 'threat'
  | 'level_4_critical'
  | 'sensitive_data'
  | 'inappropriate_tone'
  | (string & {}); // catch-all extensibility

/** Ação canonical decidida pelo agente — frontend mapeia para UI. */
export type ProtectionAction = 'block' | 'warn' | 'pass';

/**
 * Response canonical do protection-agent.
 *
 * - `severity = 'none'` ou `action = 'pass'` → mensagem flui sem UI extra.
 * - `severity ∈ {low, medium}` + `action = 'warn'` → ProtectionWarningBanner inline.
 * - `severity ∈ {high, critical}` + `action = 'block'` → ProtectionBlockModal.
 * - `timeoutFallback = true` → agent IA timeoutou (Opus); UI mostra estado degradado.
 *
 * **Privacidade canonical:** `regexHits` é lista de categorias detectadas pela
 * camada 0 (Item 227), NÃO texto cru. Backend Agent A não devolve PII matched
 * para o frontend (logs JS poderiam vazar). Atendente já viu o texto original
 * (digitou ele mesmo no Composer) — UI lista categorias + reason +
 * redactionSuggestion para contexto rico.
 */
export interface ProtectionValidation {
  severity: ProtectionSeverity;
  categories: ProtectionCategory[];
  reason: string;
  /**
   * Sugestão de redação alternativa quando disponível.
   * Backend retorna `string | null`; frontend trata null como ausente.
   */
  redactionSuggestion?: string | null;
  /**
   * Categorias regex canonical detectadas pela camada 0 (privacy-preserving —
   * apenas labels: `cpf`, `email`, `credit_card`, etc.; sem texto cru).
   */
  regexHits?: readonly string[];
  action: ProtectionAction;
  /** True quando IA timeoutou e fallback graceful disparou. */
  timeoutFallback?: boolean;
}

/**
 * Response canonical do `POST /v1/conversations/:id/messages`.
 *
 * **Sucesso (200/201):** corpo contém `message` (MessageItem) + opcionalmente
 * `validation` (quando severity=low/medium passou warn). Atendente vê banner
 * inline dismissible.
 *
 * **Bloqueio (400):** corpo contém `{ error: 'validation_blocked', validation }`.
 * Frontend captura via ApiError e abre ProtectionBlockModal INFORMATIVO. O
 * bloqueio é HARD no Engine (F1.3) — a única ação do atendente é editar a
 * mensagem e reenviar. NÃO há override "Enviar mesmo assim".
 */
export interface ProtectionBlockedErrorBody {
  error: 'validation_blocked';
  validation: ProtectionValidation;
  /** Inclui `message` (string ou string[]) por consistência com ApiErrorBody. */
  message?: string | string[];
  statusCode?: number;
}

/**
 * Helper canonical — decide UI a renderizar baseado em severity+action.
 *
 * - `'banner'` → ProtectionWarningBanner inline (warn flow)
 * - `'modal'` → ProtectionBlockModal (block flow)
 * - `'none'` → sem UI
 */
export type ProtectionUIDecision = 'banner' | 'modal' | 'none';

export function decideProtectionUI(
  validation: ProtectionValidation | null | undefined,
): ProtectionUIDecision {
  if (validation === null || validation === undefined) return 'none';
  if (validation.action === 'pass' || validation.severity === 'none') return 'none';
  if (validation.action === 'block') return 'modal';
  if (validation.action === 'warn') return 'banner';
  return 'none';
}
